import Banner from "@shared/components/ui-kit/data-display/Banner/Banner.web";
import { BannerVariant } from "@shared/components/ui-kit/data-display/Banner/Banner.props";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { ExchangeCreditBannerSharedProps } from "@shared/modules/exchanges/components/ExchangeCreditBanner/ExchangeCreditBanner.props";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Icon from "@ui-kit/icons/Icon.web";
import { useEffect, useMemo, useState } from "react";
import useWindowSize from "@shared/hooks/useWindowSize";
import breakpoints from "@shared/theme/props/breakpoints";
import PageMaxWidth from "@ui-kit/layout/PageMaxWidth/PageMaxWidth.web";

const { md } = breakpoints;

/**
 * ExchangeCreditBanner
 *
 * Displays an informational banner showing available temporary exchange credit
 * that can be applied to a purchase for events organized by a specific venue.
 *
 * @example
 * ```tsx
 * import ExchangeCreditBanner from "@shared/modules/exchanges/components/ExchangeCreditBanner/ExchangeCreditBanner.web";
 *
 * function CheckoutPage() {
 *   const handleHowCreditsWork = () => {
 *     // Open modal or navigate to credits info page
 *     console.log("Show credits information");
 *   };
 *
 *   const handleCancel = () => {
 *     // Handle banner dismissal
 *     console.log("User dismissed the banner");
 *   };
 *
 *   return (
 *     <ExchangeCreditBanner
 *       creditAmount={25.50}
 *       currency="CAD"
 *       onHowCreditsWork={handleHowCreditsWork}
 *       onCancel={handleCancel}
 *       isDisabled={false}
 *     />
 *   );
 * }
 * ```
 */

export default function ExchangeCreditBanner(
  props: ExchangeCreditBannerSharedProps
) {
  return (
    <Banner
      variant={BannerVariant.Info}
      py="xs"
      px="md"
      data-testid="exchange-credit-banner"
      as="section"
      message={<ExchangeCreditBanner.Content {...props} />}
    />
  );
}

function ExchangeCreditBannerContent(
  props: ExchangeCreditBannerSharedProps
): JSX.Element {
  const {
    description,
    ctaLabel,
    onCta,
    onCancel,
    isDisabled = false,
    allowCancel = true,
  } = props;

  const { width: windowWidth } = useWindowSize();
  const isSmallerThanMd = useMemo(
    () => Boolean(windowWidth && windowWidth < md - 1),
    [windowWidth]
  );

  const { t } = useTranslation("exchanges");
  const [showActions, setShowActions] = useState(!isSmallerThanMd);

  useEffect(() => {
    setShowActions(!isSmallerThanMd);
  }, [isSmallerThanMd]);

  return (
    <Flex
      justifyContent="space-between"
      direction={{ base: "column", md: "row" }}
      gap={{
        base: "sm",
        md: "none",
      }}
      width="100%"
      alignItems="center"
    >
      <Flex direction="row" gap={4}>
        {description}
        <Text
          display={{
            base: "block",
            md: "none",
          }}
          onClick={() => setShowActions(!showActions)}
        >
          {!showActions ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
        </Text>
      </Flex>
      <Flex
        display={showActions ? "flex" : "none"}
        gap={2}
        w={{
          base: "100%",
          md: "fit-content",
        }}
        justifyContent={{
          base: "space-between",
          md: "center",
        }}
      >
        {allowCancel && (
          <Button.Outline
            flex={{
              base: 1,
              md: "unset",
            }}
            disabled={isDisabled}
            data-testid="exchange-credit-banner-cancel-button"
            ml="auto"
            colorPalette="darkBlue"
            size="sm"
            onClick={onCancel}
          >
            {t("Cancel exchange")}
          </Button.Outline>
        )}
        <Button
          variant="solid"
          disabled={isDisabled}
          flex={{
            base: 1,
            md: "unset",
          }}
          data-testid="exchange-credit-banner-cta-button"
          size="sm"
          colorPalette="darkBlue"
          onClick={onCta}
        >
          {ctaLabel}
        </Button>
      </Flex>
    </Flex>
  );
}

ExchangeCreditBanner.Content = ExchangeCreditBannerContent;

ExchangeCreditBanner.Public = function PublicExchangeCreditBanner(
  props: ExchangeCreditBannerSharedProps
) {
  return (
    <Banner
      variant={BannerVariant.Info}
      py="xs"
      data-testid="exchange-credit-banner"
      as="section"
      message={
        <PageMaxWidth>
          <ExchangeCreditBannerContent {...props} />
        </PageMaxWidth>
      }
    />
  );
};
